// App.js
import React, { useMemo, useState } from 'react';
import {
  SafeAreaView,
  StatusBar,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { COLORS, SPACING, SIZES, RADIUS, TYPO, GRADIENTS, SHADOWS } from './theme';

// Screens & Components
import ChessOpeningGrid from './components/OpeningSelect';
import Trainer from './components/Trainer';
import Sidebar from './components/Sidebar';
import PGNInputModal from './components/PGNInputModal';
import SvgPiece from './components/SvgPiece';

const App = () => {
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [isPGNModalVisible, setPGNModalVisible] = useState(false);

  // Simple screen state
  const [screen, setScreen] = useState('Home'); // 'Home' | 'Trainer'
  const [trainerParams, setTrainerParams] = useState(null);

  // Trainer side menu state is lifted so the global header can control it
  const [isTrainerMenuVisible, setTrainerMenuVisible] = useState(false);

  const isTrainerScreen = screen === 'Trainer' && trainerParams;

  const goHome = () => {
    setScreen('Home');
    setTrainerParams(null);
    setSidebarVisible(false);
    setTrainerMenuVisible(false);
  };

  const goTrainer = (params) => {
    setTrainerParams(params);
    setScreen('Trainer');
    setSidebarVisible(false);
    setTrainerMenuVisible(false);
  };

  const handleOpenPGN = () => {
    setPGNModalVisible(true);
    setSidebarVisible(false);
  };

  const handleSelectOpeningReset = () => {
    goHome();
  };

  const handleSubmitPGN = ({ pgn, color }) => {
    setPGNModalVisible(false);
    goTrainer({
      pgn,
      openingName: 'Custom PGN',
      playerColor: color === 'white' ? 'w' : 'b',
    });
  };

  const handleSelectOpening = (opening) => {
    goTrainer({
      pgn: opening.pgn,
      openingName: opening.name,
      playerColor: opening.playerColor || (opening.color === 'white' ? 'w' : 'b'),
    });
  };

  const headerLeft = useMemo(() => {
    if (isTrainerScreen) {
      return (
        <View style={styles.trainerLeftCluster}>
          <TouchableOpacity style={styles.headerBtn} onPress={goHome} accessibilityLabel="Back">
            <Text style={styles.headerBtnIcon}>←</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.headerBtn, styles.headerBtnSpacingRight]}
            onPress={() => setSidebarVisible(true)}
            accessibilityLabel="Open menu"
          >
            <Text style={styles.headerBtnIcon}>☰</Text>
          </TouchableOpacity>

          {/* Brand inline, moved left after burger (trainer special state) */}
          <View style={styles.brandInline} pointerEvents="none">
            <View style={styles.logoMark}>
              <SvgPiece piece={{ type: 'n', color: 'w' }} size="100%" />
            </View>
            <View style={styles.logoTextWrap}>
              <Text style={styles.logoTop}>Chess</Text>
              <Text style={styles.logoBottom}>Upgrader</Text>
            </View>
          </View>
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={styles.headerBtn}
        onPress={() => setSidebarVisible(true)}
        accessibilityLabel="Open menu"
      >
        <Text style={styles.headerBtnIcon}>☰</Text>
      </TouchableOpacity>
    );
  }, [isTrainerScreen]);

  // IMPORTANT: Center content is absolutely centered so it is truly centered,
  // independent of left cluster width.
  const headerCenter = useMemo(() => {
    if (isTrainerScreen) {
      return (
        <View style={styles.headerCenterAbsolute} pointerEvents="none">
          <Text style={styles.trainerTitle} numberOfLines={1}>
            {trainerParams?.openingName || ''}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.logoArea} pointerEvents="none">
        <View style={styles.logoMark}>
          <SvgPiece piece={{ type: 'n', color: 'w' }} size="100%" />
        </View>
        <View style={styles.logoTextWrap}>
          <Text style={styles.logoTop}>Chess</Text>
          <Text style={styles.logoBottom}>Upgrader</Text>
        </View>
      </View>
    );
  }, [isTrainerScreen, trainerParams]);

  const headerRight = useMemo(() => {
    if (isTrainerScreen) {
      return (
        <TouchableOpacity
          style={styles.headerBtn}
          onPress={() => setTrainerMenuVisible((v) => !v)}
          accessibilityLabel="Open trainer options"
        >
          <Text style={styles.headerBtnIcon}>☰</Text>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity style={styles.headerBtn} accessibilityLabel="Profile">
        <Text style={styles.headerBtnIcon}>👤</Text>
      </TouchableOpacity>
    );
  }, [isTrainerScreen]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.root}>
        <StatusBar barStyle="light-content" />

        {/* Global background */}
        <View pointerEvents="none" style={styles.bgWeb} />
        <View pointerEvents="none" style={styles.bgAccentTop} />
        <View pointerEvents="none" style={styles.bgAccentBottom} />

        {/* Watermark pieces */}
        <View pointerEvents="none" style={styles.bgWatermarkLeft}>
          <SvgPiece piece={{ type: 'q', color: 'w' }} size="100%" />
        </View>
        <View pointerEvents="none" style={styles.bgWatermarkRight}>
          <SvgPiece piece={{ type: 'k', color: 'w' }} size="100%" />
        </View>

        {/* Global Header */}
        <View style={styles.globalHeader}>
          {headerLeft}
          {headerCenter}
          {headerRight}
        </View>

        {/* Content */}
        <View style={styles.content}>
          {isTrainerScreen ? (
            <Trainer
              pgn={trainerParams.pgn}
              playerColor={trainerParams.playerColor}
              openingName={trainerParams.openingName}
              onBack={goHome}
              appHeaderHeight={SIZES.headerHeight}
              menuVisible={isTrainerMenuVisible}
              onMenuVisibleChange={setTrainerMenuVisible}
            />
          ) : (
            <ChessOpeningGrid onSelectOpening={handleSelectOpening} />
          )}
        </View>

        {/* Sidebar + PGN Modal */}
        <Sidebar
          isVisible={isSidebarVisible}
          onClose={() => setSidebarVisible(false)}
          onSelectOpening={handleSelectOpeningReset}
          onOpenPGN={handleOpenPGN}
        />

        <PGNInputModal
          visible={isPGNModalVisible}
          onClose={() => setPGNModalVisible(false)}
          onSubmit={handleSubmitPGN}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.background,
    ...Platform.select({
      web: {
        height: '100vh',
        overflow: 'hidden',
      },
      default: {},
    }),
  },
  root: {
    flex: 1,
    backgroundColor: COLORS.background,
    position: 'relative',
    ...Platform.select({
      web: {
        height: '100vh',
        overflow: 'hidden',
      },
      default: {},
    }),
  },
  content: { flex: 1, minHeight: 0 },

  bgWeb: {
    ...StyleSheet.absoluteFillObject,
    ...Platform.select({
      web: {
        backgroundImage: `${GRADIENTS.appNoiseWeb}, ${GRADIENTS.appBgWeb}`,
        backgroundAttachment: 'fixed',
      },
      default: {},
    }),
  },
  bgAccentTop: {
    position: 'absolute',
    top: -160,
    left: -140,
    width: 360,
    height: 360,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.06)',
    transform: [{ rotate: '18deg' }],
  },
  bgAccentBottom: {
    position: 'absolute',
    bottom: -210,
    right: -160,
    width: 420,
    height: 420,
    borderRadius: 999,
    backgroundColor: 'rgba(245,158,11,0.08)',
    transform: [{ rotate: '-16deg' }],
  },
  bgWatermarkLeft: {
    position: 'absolute',
    left: -40,
    top: 140,
    width: 220,
    height: 220,
    opacity: 0.06,
    transform: [{ rotate: '10deg' }],
  },
  bgWatermarkRight: {
    position: 'absolute',
    right: -70,
    top: 220,
    width: 260,
    height: 260,
    opacity: 0.05,
    transform: [{ rotate: '-12deg' }],
  },

  globalHeader: {
    height: SIZES.headerHeight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.md,
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.06)',
    position: 'relative', // enables absolute centering
  },

  headerBtn: {
    width: SIZES.headerBtn,
    height: SIZES.headerBtn,
    borderRadius: RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.headerBtnBg,
    borderWidth: 1,
    borderColor: COLORS.headerBtnBorder,
    ...SHADOWS.headerBtn,
    ...Platform.select({
      web: { backdropFilter: 'blur(10px)' },
      default: {},
    }),
  },
  headerBtnSpacingRight: { marginRight: 6 },
  headerBtnIcon: { ...TYPO.headerIcon },

  // Home: centered logo overlay
  logoArea: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },

  // Trainer: absolutely centered opening title
  headerCenterAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    // keep title clear of left brand cluster + right menu button
    paddingHorizontal: 190,
  },
  trainerTitle: {
    color: COLORS.text,
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.2,
    textAlign: 'center',
  },

  logoMark: {
    width: 22,
    height: 22,
    marginRight: 6,
    opacity: 0.95,
  },
  logoTextWrap: { alignItems: 'flex-start', justifyContent: 'center' },
  logoTop: { ...TYPO.logoTop },
  logoBottom: { ...TYPO.logoBottom, marginTop: 1 },

  trainerLeftCluster: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandInline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 2,
  },
});

export default App;
